import { daysLeftLabel, shortDate, daysUntil, urgency } from "../lib/dates";
import { FALLBACK_COLOR } from "../lib/palette";
import type { Project, Task } from "../types";

interface Props {
  task: Task;
  project: Project | undefined;
  draggable: boolean;
  onOpen: (task: Task) => void;
  onDragStart: (task: Task) => void;
  onDragEnd: () => void;
  dragging: boolean;
}

export function isOverdue(task: Task): boolean {
  if (!task.deadline || task.status === "Done") return false;
  const days = daysUntil(task.deadline);
  return days !== null && days < 0;
}

export function TaskCard({
  task,
  project,
  draggable,
  onOpen,
  onDragStart,
  onDragEnd,
  dragging,
}: Props) {
  const classes = [
    "card",
    task.status === "Done" && "card--done",
    dragging && "card--dragging",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article
      className={classes}
      style={{ ["--pc" as string]: project?.color ?? FALLBACK_COLOR }}
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", task.id);
        e.dataTransfer.effectAllowed = "move";
        onDragStart(task);
      }}
      onDragEnd={onDragEnd}
    >
      <button className="card__title" onClick={() => onOpen(task)}>
        {task.title}
      </button>
      <div className="card__meta">
        {task.deadline &&
          (task.status === "Done" ? (
            // Days left is meaningless once a task is finished, so a done
            // card shows the date it was due and stays uncoloured.
            <span title={`Due ${shortDate(task.deadline)}`}>
              {shortDate(task.deadline)}
            </span>
          ) : (
            <span
              className={`card__due card__due--${urgency(task.deadline)}`}
              title={shortDate(task.deadline)}
            >
              {daysLeftLabel(task.deadline)}
            </span>
          ))}
        {task.workPackage && <span>{task.workPackage}</span>}
        {task.activity && <span>{task.activity}</span>}
        {task.cooperateWith && <span>{task.cooperateWith}</span>}
        {task.resultLink && (
          <a
            className="card__link"
            href={task.resultLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            Result
          </a>
        )}
        {!task.resultLink && task.outcome && (
          <span title={task.outcome}>No deliverable</span>
        )}
      </div>
    </article>
  );
}
